import { Alert, Grid, Stack } from '@mui/material';
import { PropsWithChildren, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router';
import { useBoolean, useLocalStorage, useSessionStorage } from 'usehooks-ts';

import { LoadingButton } from '@/components/Form/LoadingButton';
import { Icon } from '@/components/Icon/Icon';
import { FullPageLoader } from '@/components/Layout/FullPageLoader';
import { Form } from '@/components/Layout/Layout.styled';

import { ERROR_CODE } from '@/constants/api';
import { SS_BIOMETRIC_CONSENT } from '@/constants/common';
import { TENANT_PARAM } from '@/constants/countries';
import { getTenantConfigs } from '@/constants/tenant-configs';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { setTenant } from '@/redux/configs/actions';
import { selectTerritories } from '@/redux/countries/selectors';

import { Tenant } from '@/types/common';
import { Language } from '@/types/i18n';
import { CountryISO } from '@/types/places';
import { ClientType } from '@/types/user';

import { getAPIErrorCode, getAPIErrorMessage } from '@/utils/api';
import { getDataTestId, openICBLinkByPlatform } from '@/utils/browser';
import { isCaymanDomain } from '@/utils/domain';
import { getTenantForCountryCode } from '@/utils/languages';
import { isCaymanTenant } from '@/utils/tenant';

import { EXC_COUNTRIES } from '../../constant/countries';
import { useEXCRequestOTP } from '../../hooks/useEXCRequestOTP';
import { useRequestOTP } from '../../hooks/useRequestOTP';
import { CountrySelectionForm as CountrySelectionFormType } from '../../types/form';
import { GDPRDrawer } from '../drawers/GDPRDrawer';
import { BranchSelect } from './BranchSelect';
import { CountrySelect, LS_COUNTRY_KEY } from './CountrySelect';
import { CustomerTypeSelect } from './CustomerTypeSelect';
import { EmailInput } from './EmailInput';
import { LanguageSelect } from './LanguageSelect';

type CountrySelectionFormProps = {
  onCountryChange?: (countryCode: string) => void;
} & PropsWithChildren;

const IGNORE_ERROR_CODES = new Set([ERROR_CODE.EXC_IS_NOT_ALLOWED]);

export function CountrySelectionForm({
  children,
  onCountryChange,
}: CountrySelectionFormProps) {
  const dispatch = useAppDispatch();
  const { t, switchTenant } = useTranslate();
  const [_, setBiometricConsent] = useSessionStorage(SS_BIOMETRIC_CONSENT, '');
  const { [TENANT_PARAM]: tenantParam } = useParams();

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const jwe = searchParams.get('jwe');
  const customerTypeParam = searchParams.get('customerType');

  const { territoryByCountryCode } = useAppSelector(selectTerritories);
  const [savedCountry] = useLocalStorage(LS_COUNTRY_KEY, '');

  const formMethods = useForm<CountrySelectionFormType>({
    defaultValues: {
      countryCode: isCaymanDomain ? CountryISO.Cayman : savedCountry,
      customerType: getCustomerType(customerTypeParam || ''),
      branchId: '',
      email: '',
      language: '' as Language,
    },
    mode: 'onChange',
  });

  const {
    watch,
    formState: { isValid },
    getValues,
    setValue,
    handleSubmit,
  } = formMethods;

  const customerType = watch('customerType');
  const countryCode = watch('countryCode');

  const selectedTenant = useMemo(
    () => getTenantForCountryCode(territoryByCountryCode, countryCode),
    [countryCode, territoryByCountryCode],
  );

  const tenantConfig = getTenantConfigs(selectedTenant);
  const isExcTenant = EXC_COUNTRIES.includes((countryCode || '').toLowerCase());
  const isExcCustomer = customerType === ClientType.Existing;
  const isNewCustomer = customerType === ClientType.New;

  const { requestExcOtp, isValidatingExc } = useEXCRequestOTP({
    getValues,
    excTenant: tenantParam,
  });

  const onEXCNotAllowed = () => {
    navigateToICB();
  };

  const {
    mutate: requestOtp,
    isPending: isOtpRequesting,
    error,
    reset,
  } = useRequestOTP({ formValues: getValues(), onEXCNotAllowed });

  const {
    value: isConsentConfirmed,
    setTrue: consent,
    setFalse: resetConsent,
  } = useBoolean(false);

  const {
    value: shouldShowGDPRDrawer,
    setTrue: showGDPRDrawer,
    setFalse: hideGDPRDrawer,
  } = useBoolean(false);

  const navigateToICB = () => {
    openICBLinkByPlatform(tenantConfig.links);
  };

  const onGetStarted = () => {
    if (isExcCustomer) {
      navigateToICB();
      return;
    }
    reset();

    if (!selectedTenant) return;

    const [language, email, branchId] = getValues([
      'language',
      'email',
      'branchId',
    ]);

    dispatch(
      setTenant({
        tenant: selectedTenant,
        language,
      }),
    );
    requestOtp({
      email,
      tenant: selectedTenant,
      branchId,
    });
  };

  const onConsent = () => {
    consent();
    hideGDPRDrawer();
    setBiometricConsent('true');
    onGetStarted();
    if (isCaymanTenant(selectedTenant)) {
      setBiometricConsent('true');
    }
  };

  const onNotConsent = () => {
    resetConsent();
    hideGDPRDrawer();
    if (isCaymanTenant(selectedTenant)) {
      setBiometricConsent('false');
    }
  };

  const onSubmit = handleSubmit(() => {
    if (!tenantConfig.shouldShowGDPRDrawer || isConsentConfirmed) {
      onGetStarted();
    } else {
      showGDPRDrawer();
    }
  });

  useEffect(() => {
    onCountryChange?.(countryCode);
  }, [countryCode, onCountryChange]);

  useEffect(() => {
    if (territoryByCountryCode) {
      if (isCaymanDomain) {
        switchTenant(Tenant.Cayman);
        setValue('countryCode', CountryISO.Cayman);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territoryByCountryCode, tenantParam, setValue, dispatch]);

  useEffect(() => {
    if (token) {
      requestExcOtp({ token, tenant: tenantParam as Tenant, jwe });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (isValidatingExc) return <FullPageLoader />;

  return (
    <>
      <FormProvider {...formMethods}>
        <Form component="form" onSubmit={onSubmit}>
          {children}
          <Grid container columnSpacing={1.5} rowSpacing={2}>
            {/* Country Selection */}
            <CountrySelect disabled={isOtpRequesting} />

            {/* Customer Type Selection */}
            {selectedTenant && isExcTenant && <CustomerTypeSelect />}

            {/* Branch Selection & Email Input
              Display when: country selected and (non-ExC tenant or EXC new customer) */}
            {countryCode && (!isExcTenant || isNewCustomer) && (
              <>
                <BranchSelect
                  disabled={
                    isOtpRequesting && !territoryByCountryCode[countryCode]
                  }
                />
                <EmailInput disabled={isOtpRequesting} />
              </>
            )}

            {/* Language Selection - Only display if the tenant has multiple languages */}
            <LanguageSelect disabled={isOtpRequesting} />

            {error && !IGNORE_ERROR_CODES.has(getAPIErrorCode(error)) && (
              <Grid item xs={12}>
                <Alert
                  icon={<Icon name="alert" />}
                  color="error"
                  variant="standard"
                >
                  {getAPIErrorMessage(error)}
                </Alert>
              </Grid>
            )}
          </Grid>

          <Stack direction="column" alignItems="stretch" gap={2}>
            <LoadingButton
              {...getDataTestId(`country-selection-action-continue`)}
              disabled={(isExcTenant && !customerType) || !isValid}
              fullWidth
              isLoading={isOtpRequesting}
              type="submit"
            >
              {t('countrySelect.form.getStartBtn')}
            </LoadingButton>
          </Stack>
        </Form>
      </FormProvider>

      <GDPRDrawer
        open={shouldShowGDPRDrawer}
        onClose={hideGDPRDrawer}
        onConsent={onConsent}
        onNotConsent={onNotConsent}
      />
    </>
  );
}

const ALLOWED_VALUES = new Set([ClientType.New, ClientType.Existing]);

function getCustomerType(customerType?: string): ClientType | '' {
  if (!customerType) return '';

  return ALLOWED_VALUES.has(customerType as ClientType)
    ? (customerType as ClientType)
    : '';
}
